import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DatePipe, DecimalPipe, LowerCasePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DevicesService, MeasurementsService } from '@cosmos/angular/api';
import { MeasurementRefreshService, PanelService, RBACService, StateService } from '@cosmos/angular/utils';
import { Device, Measurement, Project, User } from '@cosmos/shared';
import { ErrorService } from '@cosmos/angular/utils';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { EditMeasurementForm, MeasurementChangeDeviceForm } from '../../dynamic-form/forms/measurement.form';
import { DynamicFormDialog } from '../../dynamic-form/dialog-form/dialog-form.component';
import { MeasurementInfoCardComponent } from '../../components/measurement-info-card/measurement-info-card.component';
import { DividerModule } from 'primeng/divider';
import { CardModule } from 'primeng/card';
import { TagModule } from 'primeng/tag';
import { SelectButtonModule } from 'primeng/selectbutton';
import { ButtonModule } from 'primeng/button';
import { DynamicLineChartComponent } from '../../data/charts/dynamic-line-chart/dynamic-line-chart.component';
import type { DatapointClickEvent } from '../../data/charts/multi-line-chart/multi-line-chart.component';
import { MeasurementActionsMenuComponent, MeasurementAction } from '../../components/measurement-actions-menu/measurement-actions-menu.component';
import { SafecorDatapointAdapter, SafecorRow } from '../datapoint/adapters/safecor-datapoint.adapter';
import { DatapointDetailsComponent } from '../datapoint/datapoint-details/datapoint-details.component';
import { ShmRescheduleDialogComponent } from '../shm-reschedule-dialog/shm-reschedule-dialog.component';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogService, DynamicDialogModule } from 'primeng/dynamicdialog';

export type ElectrodeSelection = 'all' | 1 | 2;

/** Per-reading variables, with the unit shown next to the value. */
const READING_VARIABLES = [
  { key: 'temperature', unit: '°C', format: '1.2-2' },
  { key: 'humidity', unit: '%', format: '1.2-2' },
  { key: 'potential', unit: 'mV', format: '1.2-2' },
  { key: 'rp', unit: 'kΩ', format: '1.2-2' },
  { key: 'rohm', unit: 'Ω', format: '1.2-2' },
  { key: 'last_ice', unit: '', format: '1.2-2' },
  { key: 'quality', unit: '', format: '1.0-0' },
] as const;

/** The four plots, in reading order (2 × 2 on desktop). */
const CHARTS = [
  { key: 'potential', titleKey: 'measurement.corrosion.corrosion-potential.chart.title' },
  { key: 'rp', titleKey: 'measurement.corrosion.corrosion-rp.chart.title' },
  { key: 'rohm', titleKey: 'measurement.corrosion.corrosion-rohm.chart.title' },
  { key: 'last_ice', titleKey: 'measurement.corrosion.corrosion-last-ice.chart.title' },
] as const;

@Component({
  selector: 'cosmos-safecor-measurement',
  templateUrl: './safecor-measurement.component.html',
  imports: [
    DatePipe, DecimalPipe, LowerCasePipe, FormsModule, ButtonModule, ConfirmDialogModule, DynamicDialogModule,
    DividerModule, CardModule, TagModule, SelectButtonModule, TranslatePipe,
    DynamicLineChartComponent, MeasurementActionsMenuComponent, MeasurementInfoCardComponent,
  ],
  providers: [ConfirmationService, DialogService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SafecorMeasurementComponent {
  private readonly errorService = inject(ErrorService);
  readonly rbac = inject(RBACService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly dialogService = inject(DialogService);
  private readonly translateService = inject(TranslateService);
  private readonly measurementsService = inject(MeasurementsService);
  private readonly devicesService = inject(DevicesService);
  private readonly stateService = inject(StateService);
  private readonly refreshService = inject(MeasurementRefreshService);
  private readonly panelService = inject(PanelService);

  readonly measurementId = input<string>();
  readonly data = input<string>();

  // Signals — mutations trigger OnPush re-render
  readonly measurement = signal<Measurement | null>(null);
  readonly device = signal<Device | null>(null);
  readonly project = signal<Project | null>(null);

  /** Which electrode(s) the plots, last-reading values and datapoint viewer show. */
  readonly electrodes = signal<ElectrodeSelection>('all');
  readonly electrodeOptions: { labelKey: string; value: ElectrodeSelection }[] = [
    { labelKey: 'measurement.corrosion.electrodes.all', value: 'all' },
    { labelKey: 'measurement.corrosion.electrodes.electrode1', value: 1 },
    { labelKey: 'measurement.corrosion.electrodes.electrode2', value: 2 },
  ];

  readonly charts = CHARTS;
  readonly readingVariables = READING_VARIABLES;

  /** Readings of the selected electrode(s) inside the reschedule window
   *  (`rangeData`), time-sorted. Single source for plots and viewer. */
  readonly rows = computed<SafecorRow[]>(() => {
    const m = this.measurement();
    const sel = this.electrodes();
    if (!m?.data) return [];
    const pick = (n: 1 | 2): SafecorRow[] =>
      sel === 'all' || sel === n
        ? ((m.data[n === 1 ? 'electrode1' : 'electrode2']?.rangeData ?? []) as any[]).map((d) => ({ ...d, electrode: n }))
        : [];
    return [...pick(1), ...pick(2)].sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime());
  });

  /** Chart input: one shared timeline, and per variable a combined column
   *  (`potential_all`, both electrodes as one series) plus one column per electrode
   *  (`potential_1`, `potential_2`) that is null on the other electrode's rows. */
  readonly plotData = computed(() => {
    const rows = this.rows();
    const out: Record<string, any[]> = { time: rows.map((r) => r.time) };
    for (const { key } of READING_VARIABLES) {
      out[`${key}_all`] = rows.map((r) => r[key] ?? null);
      for (const n of [1, 2] as const) {
        out[`${key}_${n}`] = rows.map((r) => (r.electrode === n ? (r[key] ?? null) : null));
      }
    }
    return out;
  });

  /** Datasets/labels per plot for the current selection. */
  readonly chartSeries = computed(() => {
    const sel = this.electrodes();
    return Object.fromEntries(
      CHARTS.map(({ key }) => [
        key,
        // "all" is ONE combined series; a single electrode is its own series.
        { datasets: [`${key}_${sel}`], labels: [key] },
      ]),
    ) as Record<string, { datasets: string[]; labels: string[] }>;
  });

  /** Latest reading of the selection, for the "last reading" block. */
  readonly lastReading = computed(() => {
    const rows = this.rows();
    return rows.length ? rows[rows.length - 1] : null;
  });

  readonly readingCounts = computed(() => {
    const d = this.measurement()?.data;
    return { 1: d?.electrode1?.rangeData?.length ?? 0, 2: d?.electrode2?.rangeData?.length ?? 0 };
  });

  readonly isCropped = computed(() => {
    const w = this.measurement()?.window;
    return !!(w?.startTs || w?.endTs);
  });

  readonly tier2Actions = computed<MeasurementAction[]>(() => {
    const m = this.measurement();
    return m?.active
      ? [{ labelKey: 'measurement.reschedule.window.button', icon: 'fa-solid fa-clock-rotate-left', action: () => this.openRescheduleDialog() }]
      : [];
  });

  user: User;

  constructor() {
    this.stateService.currentUser$
      .pipe(takeUntilDestroyed())
      .subscribe(x => { this.user = x; });

    effect(() => {
      if (this.measurementId()) {
        this.initData();
      }
    });
  }

  initData(): void {
    this.measurementsService.findById(this.measurementId(), '/?populate_project&populate_device=name,deviceProfile').subscribe({
      next: (response) => {
        this.project.set(response.body.project);
        this.measurement.set(response.body);
      },
      error: (e) => this.errorService.handleError(e),
      complete: () => this.getData(),
    });
  }

  getData() {
    this.device.set(this.measurement().device as Device || { name: '-' });
  }

  onDatapointClick(event: DatapointClickEvent): void {
    const m = this.measurement();
    const rows = this.rows();
    if (!m || !rows[event.index]) return;

    const adapter = new SafecorDatapointAdapter({
      measurementName: m.name,
      rows,
      startIndex: event.index,
      series: READING_VARIABLES.map((v) => ({
        key: v.key, unit: v.unit || undefined,
        label: this.translateService.instant('measurement.corrosion.' + v.key),
      })),
      electrodeLabel: (n) => this.translateService.instant('measurement.corrosion.electrodes.electrode' + n),
      // Plotted fields are suffixed per electrode (`potential_1`, `potential_all`) — the viewer wants the bare variable.
      highlightKey: event.variable.replace(/_(1|2|all)$/, ''),
    });
    this.panelService.initialize(DatapointDetailsComponent, { adapter });
  }

  openRescheduleDialog(): void {
    const m = this.measurement();
    if (!m?.data) return;
    const times = [m.data.electrode1?.allData ?? [], m.data.electrode2?.allData ?? []]
      .flat()
      .map((d) => new Date(d.time).getTime());
    if (!times.length) {
      this.errorService.notify('warn', this.translateService.instant('measurement.reschedule.no-data'));
      return;
    }

    const ref = this.dialogService.open(ShmRescheduleDialogComponent, {
      header: this.translateService.instant('measurement.reschedule.window.title'),
      modal: true, closable: true, width: '30vw',
      breakpoints: { '960px': '75vw', '640px': '90vw' },
      data: {
        min: new Date(Math.min(...times)),
        max: new Date(Math.max(...times)),
        current: {
          startTs: m.window?.startTs ? new Date(m.window.startTs) : null,
          endTs: m.window?.endTs ? new Date(m.window.endTs) : null,
        },
      },
    });
    ref.onClose.subscribe((result: { startTs: string | null; endTs: string | null } | undefined) => {
      if (result === undefined) return;
      this.measurementsService.rescheduleWindow(this.measurementId(), result).subscribe(
        this.errorService.handleMutation({
          successMessageKey: 'measurement.reschedule.window.success',
          onSuccess: () => this.initData(),
        }),
      );
    });
  }

  editMeasurement() {
    this.openDialog();
  }

  finishMeasurement($event: Event) {
    this.confirmationService.confirm({
      target: $event.currentTarget as EventTarget,
      message: this.translateService.instant('measurement.finish-dialog.message'),
      icon: 'fa-solid fa-warning',
      header: this.translateService.instant('dialog.confirm.warning'),
      closable: true,
      closeOnEscape: true,
      rejectButtonProps: {
        label: this.translateService.instant('button.cancel'),
        severity: 'secondary',
        outlined: true
      },
      acceptButtonProps: {
        label: this.translateService.instant('button.finish')
      },
      accept: () => {
        this.measurementsService.finish(this.measurementId()).subscribe(
          this.errorService.handleMutation({
            successMessageKey: 'measurement.messages.finishSuccess',
            onSuccess: () => {
              this.initData();
              this.refreshService.trigger();
            },
          }),
        );
      },
      reject: () => null
    });
  }

  changeDevice(measurement: Measurement) {
    this.devicesService.find(`/?project=${this.measurement().project._id}&available=true&deviceType=DataAcquisition&deviceProfile=${this.device().deviceProfile}`).subscribe({
      next: (response) => {
        const devices = response.body;
        if (devices.length > 0) {
          const measurementChangeDeviceForm = new MeasurementChangeDeviceForm(this.translateService, devices, this.user, {});
          const dialogRef = this.dialogService.open(DynamicFormDialog, {
            data: { form: measurementChangeDeviceForm },
            header: this.translateService.instant('measurement.change_device'),
            modal: true, closable: true, width: '50vw',
            breakpoints: { '960px': '75vw', '640px': '90vw' },
          });
          dialogRef.onClose.subscribe(result => {
            if (result !== undefined) {
              this.measurementsService.changeDevice(this.measurement()._id, result).subscribe(
                this.errorService.handleMutation({
                  successMessageKey: 'measurement.messages.updateSuccess',
                  onSuccess: () => this.initData(),
                }),
              );
            }
          });
        } else {
          this.errorService.notify('warn', this.translateService.instant('devices.not-found'));
        }
      },
      error: (e) => {
        if (e.error.message == 'Devices not Found') {
          this.errorService.notify('warn', this.translateService.instant('devices.not-found'));
        }
      },
    });
  }

  changeDeviceSender(device: Device) {
    this.measurementsService.changeDevice(this.measurement()._id, device).subscribe(
      this.errorService.handleMutation({
        successMessageKey: 'measurement.messages.updateSuccess',
        onSuccess: () => this.initData(),
      }),
    );
  }

  openDialog() {
    const m = this.measurement();
    const editMeasurementForm = new EditMeasurementForm(this.translateService, m, this.user, {
      visualizationConfig: this.project().visualizationConfig, measurementType: m.measurementType
    });
    const dialogRef = this.dialogService.open(DynamicFormDialog, {
      data: { form: editMeasurementForm, dialogTitle: this.translateService.instant('dialog.title.edit.measurement') },
      header: this.translateService.instant('dialog.title.edit.measurement'),
      modal: true, closable: true, width: '50vw',
      breakpoints: { '960px': '75vw', '640px': '90vw' },
    });

    dialogRef.onClose.subscribe(result => {
      if (result !== undefined) {
        this.measurementsService.update([m._id, result]).subscribe(
          this.errorService.handleMutation({
            successMessageKey: 'measurement.messages.updateSuccess',
            onSuccess: () => this.initData(),
          }),
        );
      }
    });
  }
}
